import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

// Matches the prototype's `.kpi-row`/`.kpi`/`.kpi-lbl`/`.kpi-val` tiles —
// see ARCHITECTURE_DECISIONS.md. Shared by Usage and Subscription: both
// pages' real `render*` functions in usage-billing.html (not the file's
// unused `.sub-pane-details` CSS/`details()` data, which nothing ever
// renders — see ARCHITECTURE_DECISIONS.md) build this exact tile shape.
const TONE_TEXT_CLASS: Record<'ok' | 'danger', string> = {
  danger: 'text-[var(--danger)]',
  ok: 'text-[var(--accent)]',
}

// `.kpi-val` is 20px; the prototype sets `font-size:17px` inline on its
// status and date KPIs and on every value of the Expired Subscription row
// (`renderExpiredSubscription`, the trial renders).
const SIZE_TEXT_CLASS: Record<'default' | 'compact', string> = {
  default: 'text-[20px]',
  compact: 'text-[17px]',
}

/** One KPI tile (MS-819 shared primitives: `size`, `unit` and null in one
 * change, C-24).
 * - `value: null` renders the em dash (`common.dash`) — no unit, no tone, no
 *   sub line, the way the prototype's dashed KPIs render.
 * - `unit` is the prototype's lighter `.kpi-unit` qualifier after the value
 *   (12px / 500 / tertiary), e.g. " / year".
 * - `size="compact"` is the 17px value. */
export function Kpi({
  label,
  value,
  sub,
  unit,
  tone,
  size = 'default',
}: Readonly<{
  label: ReactNode
  value: string | null
  sub?: string
  unit?: string
  tone?: 'ok' | 'danger'
  size?: 'default' | 'compact'
}>) {
  const { t } = useTranslation()
  const empty = value === null
  const toneClass = tone && !empty ? TONE_TEXT_CLASS[tone] : 'text-[var(--text-primary)]'
  return (
    <div className="flex-1 min-w-[160px] bg-[var(--bg-app)] border border-[var(--border-light)] rounded-[10px] px-3.5 py-3">
      <div className="text-[10.5px] uppercase tracking-wide text-[var(--text-tertiary)]">{label}</div>
      <div className={`${SIZE_TEXT_CLASS[size]} font-bold mt-0.5 tabular-nums ${toneClass}`}>
        {empty ? t('common.dash') : value}
        {unit && !empty && (
          <span className="text-[12px] font-medium text-[var(--text-tertiary)]">{unit}</span>
        )}
      </div>
      {sub && !empty && <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">{sub}</div>}
    </div>
  )
}

export function KpiRow({ children }: Readonly<{ children: ReactNode }>) {
  return <div className="flex gap-3 flex-wrap mb-4 last:mb-0">{children}</div>
}
